import { RURAL_URBAN, USER_TYPE } from './constant';

export const setIsFieldDisabledOrRequired = (roleDetails, properties, ...keys) => {
  return keys.reduce((acc, key) => {
    acc[key] = { ...roleDetails[key], ...properties };
    return acc;
  }, { ...roleDetails });
};

export const getLockedHierarchyFields = (userTypeName, loggedInUserDetails = {}) => {
  const lockedFields = new Set();
  const ruralUrbanName = loggedInUserDetails?.urban_rural_name?.toLowerCase();

  switch (userTypeName) {
    case USER_TYPE.STATE_LEVEL:
      lockedFields.add('state');
      break;
    case USER_TYPE.DISTRICT_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      break;
    case USER_TYPE.ZONE_DIVISION_LEVEL:
      lockedFields.add('state');
      lockedFields.add('zone_div_circle');
      break;
    case USER_TYPE.ULB_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      lockedFields.add('rural_urban');
      lockedFields.add('ulb');
      break;
    case USER_TYPE.SUB_DISTRICT_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      lockedFields.add('sub_district');
      break;
    case USER_TYPE.BLOCK_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      lockedFields.add('sub_district');
      lockedFields.add('rural_urban');
      lockedFields.add('block');
      break;
    case USER_TYPE.HEALTH_BLOCK_SUB_DIVISION_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      if (loggedInUserDetails?.health_block_id) {
        lockedFields.add('health_block');
      }
      if (loggedInUserDetails?.sub_division_id) {
        lockedFields.add('sub_division');
      }
      break;
    case USER_TYPE.FACILITY_LEVEL:
    case USER_TYPE.FIELD_LEVEL:
      lockedFields.add('state');
      lockedFields.add('district');
      if (ruralUrbanName === RURAL_URBAN.RURAL.toLowerCase()) {
        lockedFields.add('rural_urban');
        if (loggedInUserDetails?.sub_district_id) {
          lockedFields.add('sub_district');
        }
        if (loggedInUserDetails?.block_id) {
          lockedFields.add('block');
        }
        if (loggedInUserDetails?.gram_panchayat_id) {
          lockedFields.add('gram_panchayat');
        }
      }
      if (ruralUrbanName === RURAL_URBAN.URBAN.toLowerCase()) {
        lockedFields.add('rural_urban');
        if (loggedInUserDetails?.ulb_id) {
          lockedFields.add('ulb');
        }
        if (loggedInUserDetails?.ward_id) {
          lockedFields.add('ward');
        }
      }
      if (loggedInUserDetails?.health_block_id) {
        lockedFields.add('health_block');
      }
      if (loggedInUserDetails?.sub_division_id) {
        lockedFields.add('sub_division');
      }
      break;
    default:
      break;
  }

  return lockedFields;
};

export const shouldLockLoggedInHierarchyField = (userTypeName, fieldName, loggedInUserDetails = {}) => {
  return getLockedHierarchyFields(userTypeName, loggedInUserDetails).has(fieldName);
};

export const applyFieldPatches = (state, patches = {}) => {
  return Object.keys(patches).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        ...(acc[key] || {}),
        ...patches[key],
      },
    }),
    { ...state },
  );
};

export const applyFieldRuleGroups = (state, ruleGroups = []) => {
  return ruleGroups.reduce((updatedState, ruleGroup) => {
    const { keys = [], properties = {} } = ruleGroup || {};
    if (!keys.length) {
      return updatedState;
    }
    return setIsFieldDisabledOrRequired(updatedState, properties, ...keys);
  }, { ...state });
};

export const resetFieldsState = (state, fieldsToUpdate) => {
  const updatedState = { ...state };
  fieldsToUpdate.forEach((field) => {
    updatedState[field] = {
      ...updatedState[field],
      value: '',
      id: '',
      code: '',
      message: '',
      isError: false,
    };
  });
  return updatedState;
};

export const findMatchedOption = (options = [], id) => {
  if (!id || !options?.length) return undefined;
  return options.find((option) => String(option.id) === String(id));
};

export const mapOptions = (items = [], mapper) => {
  if (!Array.isArray(items)) return [];
  return items.map(mapper);
};

export const getSelectedOptionsMeta = (selectedValues = [], options = [], includeCompositeRoleId = false) => {
  const normalizedSelected = Array.isArray(selectedValues)
    ? selectedValues
    : selectedValues
      ? [selectedValues]
      : [];
  const selectedOptions = options.filter((option) => normalizedSelected.includes(option?.value));

  const ids = selectedOptions.map((option) => option?.id);
  const codes = selectedOptions.map((option) => option?.code);

  if (!includeCompositeRoleId) {
    return { ids, codes };
  }

  const compositeRoleIds = selectedOptions.map((option) => option?.compositeRoleId);
  return { ids, codes, compositeRoleIds };
};

export const applyFieldOptions = (state, fieldName, options = [], extraProps = {}) => {
  return applyFieldPatches(state, {
    [fieldName]: {
      '#options': options,
      ...extraProps,
    },
  });
};

export const getFacilityTypeNameById = (facilityTypeOptions = [], facilityTypeId) => {
  return facilityTypeOptions.find((item) => item.id === facilityTypeId)?.value || '';
};

export const mapStateOptions = (items = []) => {
  return mapOptions(items, (key) => ({
    value: `${key.state_name}-${key.state_code}`,
    label: `${key.state_name}-${key.state_code}`,
    id: key.state_id,
    code: key.state_code,
  }));
};

export const mapZoneDivisionOptions = (items = []) => {
  return mapOptions(items, (key) => {
    const zoneName = key.zone_name ?? key.zone_div_circle_name ?? '';
    const zoneCode = key.zone_code ?? key.zone_div_circle_code ?? '';
    const zoneId = key.zone_id ?? key.zone_div_circle_id ?? '';

    return {
      value: `${zoneName}-${zoneCode}`,
      label: `${zoneName}-${zoneCode}`,
      id: zoneId,
      code: zoneCode,
    };
  });
};

export const mapDistrictOptions = (items = []) => {
  return mapOptions(items, (key) => ({
    value: `${key.district_name}-${key.district_code}`,
    label: `${key.district_name}-${key.district_code}`,
    id: key.district_id,
    code: key.district_code,
  }));
};

export const mapUlbOptions = (items = []) => {
  return mapOptions(items, (key) => ({
    value: `${key.ulb_name}-${key.ulb_code}`,
    label: `${key.ulb_name}-${key.ulb_code}`,
    id: key.ulb_id,
    code: key.ulb_code,
  }));
};

export const mapSubDistrictOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.sub_district_name}-${option.sub_district_code}`,
    label: `${option.sub_district_name}-${option.sub_district_code}`,
    id: option.sub_district_id,
    code: option.sub_district_code,
  }));
};

export const mapHealthBlockOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.heal_block_name}-${option.heal_block_code}`,
    label: `${option.heal_block_name}-${option.heal_block_code}`,
    id: option.health_block_id,
    code: option.heal_block_code,
  }));
};

export const mapSubDivisionOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.sub_division_name}-${option.sub_division_code}`,
    label: `${option.sub_division_name}-${option.sub_division_code}`,
    id: option.sub_division_id,
    code: option.sub_division_code,
  }));
};

export const mapBlockOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.block_name}-${option.block_code}`,
    label: `${option.block_name}-${option.block_code}`,
    id: option.block_id,
    code: option.block_code,
  }));
};

export const mapWardOptions = (items = [], ulbValue = '') => {
  return mapOptions(items, (key) => ({
    value: `${ulbValue}-${key.ward_name}-${key.ward_code}`,
    label: `${ulbValue}-${key.ward_name}-${key.ward_code}`,
    id: key.ward_id,
    code: key.ward_code,
  }));
};

export const mapVillageOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.village_name}-${option.village_code}`,
    label: `${option.village_name}-${option.village_code}`,
    id: option.village_id,
    code: option.village_code,
  }));
};

export const mapGramPanchayatOptions = (items = []) => {
  return mapOptions(items, (option) => ({
    value: `${option.gram_panchayat_name}-${option.gram_panchayat_code}`,
    label: `${option.gram_panchayat_name}-${option.gram_panchayat_code}`,
    id: option.gram_panchayat_id,
    code: option.gram_panchayat_code,
  }));
};

export const mapRuralUrbanOptions = (items = []) => {
  return mapOptions(items, (key) => ({
    value: key?.urban_rural_name === 'rural' ? 'Rural' : 'Urban',
    label: key?.urban_rural_name === 'rural' ? 'Rural' : 'Urban',
    id: key?.urban_rural_id,
    code: key?.urban_rural_code ?? key?.urban_rural_id,
  }));
};
